import { DownloadPassportButton } from '@/features/passport/DownloadPassportButton';
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getAllPublicStructures, getPublicStructureBySlug, getStructuresByIds } from '../firebase/firestore';
import { extractAccessibilityScoresFromStructure } from '../utils/portalStructures';
import idSlugData from '../data/structures-id-and-slug.updated.json';
import { auth } from '../firebase/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { Skeleton } from './ui/skeleton';
import { Checkbox } from './ui/checkbox';
import {
  Building2, CheckCircle, Share, Download,
  Bus, Car, DoorOpen, Shield, Navigation, Monitor,
  Users, Utensils, Gamepad2, Wrench, HeadphonesIcon, Megaphone,
  Eye, Volume2, FileText, BookOpen, ArrowLeft, Home, WifiIcon,
  Settings, Copy, Globe, QrCode, Code, Settings2, Accessibility, Search, ShieldQuestion
} from 'lucide-react';
import { StructureAttributionBar } from './structure/StructureAttributionBar';
import { StructureHeader } from './structure/StructureHeader';
import { WidgetPreview } from './structure/WidgetPreview';
import { WidgetCustomizer } from './structure/WidgetCustomizer';
import { ShareEmbedCard } from './structure/ShareEmbedCard';
import { QuickActionsCard } from './structure/QuickActionsCard';
import { WidgetStatsCard } from './structure/WidgetStatsCard';
import { ComplianceBarChart } from './structure/ComplianceBarChart';
import { UrgentImprovementList } from './structure/UrgentImprovementList';
import { RequirementsSummaryGrid } from './structure/RequirementsSummaryGrid';
import { ImprovementSidebar } from './structure/ImprovementSidebar';
import { ModuleSuggestionsList } from './structure/ModuleSuggestionsList';
import { getSuggestionsWithSource } from '../lib/suggestions';
import { type SuggestionStatus, type SuggestionsV2, type SuggestionsV2Summary } from '@/lib/suggestionsV2';
import { isSuggestionsV2Enabled as getSuggestionsV2Flag } from '@/lib/env';
import { updateSuggestionStatusSafe, updateSuggestionStatusesSafe } from '@/lib/suggestionsStatusWriter';
import { useStructureSuggestionsSummary, useStructureSuggestionsItems, useLegacySuggestions } from '@/hooks/useSuggestionsV2';
import { getPortalBranding } from '../utils/portalPriority';

// NOTE: We keep a local mock for fallback, but primary list is loaded from public/detailed_structures_index.json
const mockStructures = [
  {
    id: "1",
    slug: "accademia-carrara-culturale-museo.json",
    structure: {
      accommodation_data: {
        accommodation_name: "Accademia Carrara",
        type: "Culturale: Museo",
        address: {
          street: "Piazza Giacomo Carrara 82, 24124 Bergamo (BG)",
          city: "Bergamo",
          province: "BG"
        },
        contacts: {
          phone: "+39 035234396",
          email: "info@lacarrara.it",
          website: "https://www.lacarrara.it/"
        }
      },
      portal_administrators: {
        portal_name: "Visit Bergamo"
      }
    },
    public_infos: {
      titlets: ["Sensoriale", "Cognitiva", "Alimentare"],
      paragraphs: {
        "Come Arrivare": "L'Accademia Carrara di Bergamo ha parcheggi riservati ai disabili a 50 metri in via San Tomaso.",
        "Ingresso": "Presente ingresso secondario per persone con disabilità, è segnalato",
        "Biglietteria": "Ingresso gratuito per visitatore con disabilità, biglietto ridotto per accompagnatore.",
        "Bagni": "Presenti bagni accessibili. Presente fasciatoio.",
        "Sicurezza": "Uscite di sicurezza ben visibili. Allarme audio e visivo con luce lampeggiante."
      }
    },
    suggestions: [
      {
        categoria: "Accessibilità motoria",
        interventi: ["Studio di fattibilità per rendere ingresso principale accessibile."],
        status: "in corso"
      },
      {
        categoria: "Accessibilità sensoriale",
        interventi: ["Estensione di pannelli visivo-tattili per tutte le opere permanenti"],
        status: "da realizzare"
      }
    ]
  },
  {
    id: "2",
    structure: {
      accommodation_data: {
        accommodation_name: "Hotel Milano Central",
        type: "Alloggio: Hotel",
        address: {
          street: "Via Manzoni 31, 20121 Milano (MI)",
          city: "Milano",
          province: "MI"
        },
        contacts: {
          phone: "+39 02 76023456",
          email: "info@hotelmilanocentral.it",
          website: "https://www.hotelmilanocentral.it/"
        }
      },
      portal_administrators: {
        portal_name: "Visit Milano"
      }
    },
    public_infos: {
      titlets: ["Motoria", "Uditiva", "Visiva"],
      paragraphs: {
        "Come Arrivare": "L'hotel è situato a 200 metri dalla stazione della metropolitana Duomo, con accesso facilitato.",
        "Ingresso": "Ingresso principale completamente accessibile con porta automatica e reception abbassata.",
        "Camere": "15 camere accessibili disponibili con bagno attrezzato e spazi di manovra adeguati.",
        "Servizi": "Ascensore vocale, servizio di assistenza 24h, area colazione accessibile.",
        "Sicurezza": "Sistema di allarme visivo e sonoro in tutte le camere accessibili."
      }
    },
    suggestions: [
      {
        categoria: "Accessibilità digitale",
        interventi: ["Implementazione sito web con lettore schermo compatibile"],
        status: "pianificato"
      },
      {
        categoria: "Accessibilità cognitiva",
        interventi: ["Segnaletica semplificata con pittogrammi universali"],
        status: "da realizzare"
      }
    ]
  },
  {
    id: "3",
    structure: {
      accommodation_data: {
        accommodation_name: "Ristorante La Terrazza",
        type: "Ristorazione: Ristorante",
        address: {
          street: "Piazza Vecchia 8, 24129 Bergamo (BG)",
          city: "Bergamo",
          province: "BG"
        },
        contacts: {
          phone: "+39 035 242881",
          email: "info@laterrazzabg.it",
          website: "https://www.laterrazzabg.it/"
        }
      },
      portal_administrators: {
        portal_name: "Visit Bergamo"
      }
    },
    public_infos: {
      titlets: ["Alimentare", "Motoria", "Comunicativa"],
      paragraphs: {
        "Come Arrivare": "Raggiungibile tramite funicolare accessibile, parcheggio riservato a 100 metri.",
        "Ingresso": "Rampa di accesso laterale, porta principale con campanello di servizio.",
        "Sala": "Tavoli regolabili in altezza disponibili, corridoi ampi per il passaggio.",
        "Menu": "Menu disponibile in Braille e formato digitale accessibile, personale formato per assistenza.",
        "Servizi": "Bagno accessibile al piano terra, area fumatori accessibile."
      }
    },
    suggestions: [
      {
        categoria: "Accessibilità sensoriale",
        interventi: ["Installazione sistema di illuminazione regolabile per ipovedenti"],
        status: "in corso"
      }
    ]
  },
  {
    id: "4",
    structure: {
      accommodation_data: {
        accommodation_name: "Centro Congressi Bergamo",
        type: "Culturale: Centro Congressi",
        address: {
          street: "Via Paleocapa 7, 24122 Bergamo (BG)",
          city: "Bergamo",
          province: "BG"
        },
        contacts: {
          phone: "+39 035 210204",
          email: "info@centrocongressibg.it",
          website: "https://www.centrocongressibg.it/"
        }
      },
      portal_administrators: {
        portal_name: "Visit Bergamo"
      }
    },
    public_infos: {
      titlets: ["Uditiva", "Motoria", "Tecnologica"],
      paragraphs: {
        "Come Arrivare": "Accesso diretto da parcheggio sotterraneo con ascensore, fermata bus accessibile di fronte.",
        "Sale": "3 sale completamente accessibili con impianti audio/video per non udenti.",
        "Tecnologie": "Sistema di traduzione simultanea, loop magnetico in tutte le sale.",
        "Servizi": "Reception multilingue, servizio di accoglienza specializzato per disabilità.",
        "Catering": "Servizio catering con opzioni per ogni esigenza alimentare e allergie."
      }
    },
    suggestions: [
      {
        categoria: "Accessibilità motoria",
        interventi: ["Installazione di corrimano ergonomici nelle scale di emergenza"],
        status: "completo"
      },
      {
        categoria: "Accessibilità digitale",
        interventi: ["Aggiornamento app mobile con funzioni di accessibilità avanzate"],
        status: "da verificare"
      }
    ]
  }
];


// Structure-specific accessibility data with module-level suggestions
const mockStructureAccessibilityData = {
  "1": [ // Accademia Carrara
    { category: "Motoria", score: 85, color: "#0369a1", requirements: { completed: 17, missing: 3 } },
    { category: "Visiva", score: 72, color: "#ea580c", requirements: { completed: 13, missing: 5 } },
    { category: "Uditiva", score: 91, color: "#7c3aed", requirements: { completed: 21, missing: 2 } },
    { category: "Cognitiva", score: 68, color: "#0891b2", requirements: { completed: 15, missing: 7 } },
    { category: "Alimentare", score: 94, color: "#059669", requirements: { completed: 16, missing: 1 } },
  ],
  "2": [ // Hotel Milano Central
    { category: "Motoria", score: 88, color: "#0369a1", requirements: { completed: 22, missing: 3 } },
    { category: "Visiva", score: 79, color: "#ea580c", requirements: { completed: 19, missing: 5 } },
    { category: "Uditiva", score: 92, color: "#7c3aed", requirements: { completed: 23, missing: 2 } },
    { category: "Cognitiva", score: 82, color: "#0891b2", requirements: { completed: 18, missing: 4 } },
    { category: "Alimentare", score: 85, color: "#059669", requirements: { completed: 17, missing: 3 } },
  ],
  "3": [ // Ristorante La Terrazza - ALL categories below 75%
    { category: "Motoria", score: 68, color: "#ea580c", requirements: { completed: 11, missing: 5 } },
    { category: "Visiva", score: 72, color: "#ea580c", requirements: { completed: 13, missing: 5 } },
    { category: "Uditiva", score: 74, color: "#ea580c", requirements: { completed: 14, missing: 5 } },
    { category: "Cognitiva", score: 65, color: "#ea580c", requirements: { completed: 13, missing: 7 } },
    { category: "Alimentare", score: 71, color: "#ea580c", requirements: { completed: 12, missing: 5 } },
  ],
  "4": [ // Centro Congressi Bergamo
    { category: "Motoria", score: 89, color: "#0369a1", requirements: { completed: 25, missing: 3 } },
    { category: "Visiva", score: 85, color: "#0369a1", requirements: { completed: 17, missing: 3 } },
    { category: "Uditiva", score: 95, color: "#059669", requirements: { completed: 19, missing: 1 } },
    { category: "Cognitiva", score: 78, color: "#0369a1", requirements: { completed: 18, missing: 5 } },
    { category: "Alimentare", score: 92, color: "#059669", requirements: { completed: 23, missing: 2 } },
  ]
};

// Module-level suggestions organized by category
const mockModuleSuggestions = {
  "1": { // Accademia Carrara
    motoria: {
      "Public Transport": [
        { id: 1, description: "Installare nuova fermata autobus accessibile a 100m dall'ingresso", status: "da realizzare", priority: "medium" },
        { id: 2, description: "Migliorare segnaletica per autobus con annunci vocali", status: "in corso", priority: "low" },
        { id: 3, description: "Coordinare con trasporti pubblici per orari dedicati", status: "da verificare", priority: "low" }
      ],
      "Parking": [
        { id: 4, description: "Aggiungere 2 parcheggi riservati con migliore segnaletica", status: "da realizzare", priority: "high" },
        { id: 5, description: "Installare pavimentazione tattile dal parcheggio all'ingresso", status: "in corso", priority: "medium" }
      ],
      "Entrance": [
        { id: 6, description: "Studio di fattibilità per rendere ingresso principale accessibile", status: "in corso", priority: "high" },
        { id: 7, description: "Installare pulsante automatico apertura porta secondaria", status: "da realizzare", priority: "medium" },
        { id: 8, description: "Migliorare illuminazione ingresso secondario", status: "da verificare", priority: "low" }
      ],
      "Interior Navigation": [
        { id: 9, description: "Installare corrimano lungo i corridoi principali", status: "da realizzare", priority: "medium" },
        { id: 10, description: "Creare percorsi tattili per ipovedenti", status: "in corso", priority: "high" }
      ],
      "Elevators & Lifts": []
    },
    visiva: {
      "Visual Aids": [
        { id: 11, description: "Estensione di pannelli visivo-tattili per tutte le opere permanenti", status: "da realizzare", priority: "high" },
        { id: 12, description: "Installare illuminazione LED regolabile per ipovedenti", status: "in corso", priority: "medium" },
        { id: 13, description: "Aggiungere descrizioni audio per le principali collezioni", status: "da realizzare", priority: "medium" },
        { id: 14, description: "Creare modelli tattili delle opere scultoree più importanti", status: "da verificare", priority: "low" }
      ],
      "Wayfinding & Signage": [
        { id: 15, description: "Installare segnaletica in Braille per tutte le sale", status: "da realizzare", priority: "high" },
        { id: 16, description: "Migliorare contrasto cromatico della segnaletica esistente", status: "in corso", priority: "medium" }
      ],
      "Digital Accessibility": [
        { id: 17, description: "Rendere il sito web compatibile con lettori di schermo", status: "da realizzare", priority: "high" },
        { id: 18, description: "Sviluppare app mobile con funzioni di accessibilità", status: "da verificare", priority: "medium" },
        { id: 19, description: "Implementare QR codes con descrizioni audio delle opere", status: "da realizzare", priority: "low" }
      ]
    },
    uditiva: {
      "Audio Systems": [
        { id: 20, description: "Installare sistema di amplificazione per visite guidate", status: "da realizzare", priority: "medium" },
        { id: 21, description: "Aggiungere cuffie wireless per audiotour accessibili", status: "in corso", priority: "low" }
      ],
      "Communication Systems": [
        { id: 22, description: "Formare staff nella lingua dei segni italiana (LIS)", status: "da realizzare", priority: "medium" },
        { id: 23, description: "Installare sistema di comunicazione visiva alla reception", status: "da verificare", priority: "low" },
        { id: 24, description: "Creare video introduttivi con sottotitoli e interpretazione LIS", status: "da realizzare", priority: "high" }
      ]
    },
    cognitiva: {
      "Staff Training": [
        { id: 25, description: "Corso di formazione sull'accoglienza di persone con disabilità cognitive", status: "in corso", priority: "high" },
        { id: 26, description: "Workshop su comunicazione semplificata e chiara", status: "da realizzare", priority: "medium" }
      ],
      "Technology Access": [
        { id: 27, description: "Sviluppare percorsi di visita semplificati tramite app", status: "da verificare", priority: "low" },
        { id: 28, description: "Installare totem informativi con interfaccia intuitiva", status: "da realizzare", priority: "medium" }
      ],
      "Guest Services": [
        { id: 29, description: "Creare servizio di accompagnamento dedicato", status: "da realizzare", priority: "high" },
        { id: 30, description: "Predisporre materiali informativi in linguaggio facile", status: "in corso", priority: "medium" },
        { id: 31, description: "Organizzare visite guidate specializzate", status: "da verificare", priority: "low" }
      ]
    },
    alimentare: {
      "Food & Dining": [
        { id: 32, description: "Menu caffetteria con opzioni per allergie e intolleranze", status: "da realizzare", priority: "medium" },
        { id: 33, description: "Tavoli regolabili in altezza nell'area ristoro", status: "da verificare", priority: "low" }
      ],
      "Recreation Areas": [
        { id: 34, description: "Creare area relax accessibile con sedute adeguate", status: "da realizzare", priority: "low" },
        { id: 35, description: "Installare fontanelle ad altezza variabile", status: "da verificare", priority: "low" }
      ]
    }
  },
  "2": { // Hotel Milano Central
    motoria: {
      "Public Transport": [
        { id: 101, description: "Migliorare segnaletica per stazione metro accessibile", status: "da realizzare", priority: "medium" }
      ],
      "Parking": [
        { id: 102, description: "Aggiungere colonnine ricarica auto elettriche nei parcheggi riservati", status: "in corso", priority: "low" },
        { id: 103, description: "Installare copertura per parcheggi disabili", status: "da verificare", priority: "low" }
      ],
      "Entrance": [],
      "Interior Navigation": [
        { id: 104, description: "Estendere corrimano su tutte le scale interne", status: "da realizzare", priority: "medium" },
        { id: 105, description: "Migliorare illuminazione corridoi per ipovedenti", status: "in corso", priority: "low" }
      ],
      "Elevators & Lifts": [
        { id: 106, description: "Installare annunci vocali in tutti gli ascensori", status: "da verificare", priority: "medium" }
      ]
    },
    visiva: {
      "Visual Aids": [
        { id: 107, description: "Aggiungere numeri delle camere in Braille", status: "da realizzare", priority: "high" },
        { id: 108, description: "Installare strip LED per evidenziare gradini e dislivelli", status: "in corso", priority: "medium" }
      ],
      "Wayfinding & Signage": [
        { id: 109, description: "Migliorare contrasto della segnaletica interna", status: "da realizzare", priority: "medium" }
      ],
      "Digital Accessibility": [
        { id: 110, description: "Implementazione sito web con lettore schermo compatibile", status: "da realizzare", priority: "high" },
        { id: 111, description: "App mobile per check-in accessibile", status: "da verificare", priority: "low" }
      ]
    },
    uditiva: {
      "Audio Systems": [
        { id: 112, description: "Sistema di allarme visivo e vibratile nelle camere", status: "da realizzare", priority: "high" }
      ],
      "Communication Systems": [
        { id: 113, description: "Videocitofono con chat testuale alla reception", status: "in corso", priority: "medium" },
        { id: 114, description: "Formazione staff su comunicazione per non udenti", status: "da verificare", priority: "low" }
      ]
    },
    cognitiva: {
      "Staff Training": [
        { id: 115, description: "Corso accoglienza clienti con disabilità cognitive", status: "in corso", priority: "high" }
      ],
      "Technology Access": [
        { id: 116, description: "Semplificare interfaccia TV e aria condizionata nelle camere", status: "da realizzare", priority: "low" }
      ],
      "Guest Services": [
        { id: 117, description: "Segnaletica semplificata con pittogrammi universali", status: "da realizzare", priority: "medium" },
        { id: 118, description: "Servizio assistenza personalizzata per ospiti con disabilità", status: "da verificare", priority: "medium" }
      ]
    },
    alimentare: {
      "Food & Dining": [
        { id: 119, description: "Menu colazione con icone per allergie e preferenze", status: "da realizzare", priority: "medium" },
        { id: 120, description: "Tavoli regolabili nell'area colazione", status: "da verificare", priority: "low" }
      ],
      "Recreation Areas": []
    }
  },
  "3": { // Ristorante La Terrazza
    motoria: {
      "Public Transport": [
        { id: 201, description: "Coordinamento con funicolare per orari agevolati", status: "da realizzare", priority: "low" }
      ],
      "Parking": [
        { id: 202, description: "Riservare ulteriori posti auto più vicini al ristorante", status: "da realizzare", priority: "high" },
        { id: 203, description: "Migliorare illuminazione del parcheggio riservato", status: "in corso", priority: "medium" }
      ],
      "Entrance": [
        { id: 204, description: "Installare piattaforma elevatrice per superare i gradini", status: "da realizzare", priority: "high" },
        { id: 205, description: "Allargare la rampa di accesso laterale", status: "da verificare", priority: "medium" }
      ],
      "Interior Navigation": [
        { id: 206, description: "Riorganizzare layout sala per migliorare accessibilità", status: "da realizzare", priority: "high" }
      ],
      "Elevators & Lifts": []
    },
    visiva: {
      "Visual Aids": [
        { id: 207, description: "Installazione sistema di illuminazione regolabile per ipovedenti", status: "in corso", priority: "high" },
        { id: 208, description: "Menu in Braille e caratteri ingranditi", status: "da realizzare", priority: "medium" }
      ],
      "Wayfinding & Signage": [
        { id: 209, description: "Segnaletica tattile per identificare bagni e uscite", status: "da realizzare", priority: "medium" }
      ],
      "Digital Accessibility": [
        { id: 210, description: "QR code menu con compatibilità screen reader", status: "da verificare", priority: "low" }
      ]
    },
    uditiva: {
      "Audio Systems": [
        { id: 211, description: "Sistema di riduzione rumore ambiente", status: "da realizzare", priority: "medium" }
      ],
      "Communication Systems": [
        { id: 212, description: "Tablet per comunicazione scritta con il personale", status: "da realizzare", priority: "low" },
        { id: 213, description: "Formazione staff per comunicazione gestuale base", status: "da verificare", priority: "medium" }
      ]
    },
    cognitiva: {
      "Staff Training": [
        { id: 214, description: "Formazione personale su accoglienza inclusiva", status: "da realizzare", priority: "high" },
        { id: 215, description: "Protocolli per assistenza clienti con disabilità cognitive", status: "in corso", priority: "medium" }
      ],
      "Technology Access": [],
      "Guest Services": [
        { id: 216, description: "Menu semplificato con immagini dei piatti", status: "da realizzare", priority: "medium" },
        { id: 217, description: "Servizio di accompagnamento al tavolo dedicato", status: "da verificare", priority: "low" }
      ]
    },
    alimentare: {
      "Food & Dining": [
        { id: 218, description: "Carta allergie e intolleranze completa e aggiornata", status: "da realizzare", priority: "high" },
        { id: 219, description: "Opzioni menu per diverse esigenze nutrizionali", status: "in corso", priority: "medium" },
        { id: 220, description: "Stoviglie adattive per persone con difficoltà motorie", status: "da verificare", priority: "low" }
      ],
      "Recreation Areas": [
        { id: 221, description: "Area esterna accessibile con vista panoramica", status: "da realizzare", priority: "medium" }
      ]
    }
  },
  "4": { // Centro Congressi Bergamo
    motoria: {
      "Public Transport": [
        { id: 301, description: "Potenziare segnaletica per fermata bus accessibile", status: "da verificare", priority: "low" }
      ],
      "Parking": [
        { id: 302, description: "Aggiungere stazione ricarica veicoli elettrici nel parcheggio", status: "da realizzare", priority: "low" }
      ],
      "Entrance": [],
      "Interior Navigation": [
        { id: 303, description: "Installazione di corrimano ergonomici nelle scale di emergenza", status: "in corso", priority: "medium" }
      ],
      "Elevators & Lifts": []
    },
    visiva: {
      "Visual Aids": [
        { id: 304, description: "Schermi ad alto contrasto per presentazioni", status: "da verificare", priority: "low" }
      ],
      "Wayfinding & Signage": [
        { id: 305, description: "Segnaletica Braille per tutte le sale congressi", status: "da realizzare", priority: "medium" }
      ],
      "Digital Accessibility": [
        { id: 306, description: "Aggiornamento app mobile con funzioni di accessibilità avanzate", status: "da verificare", priority: "high" },
        { id: 307, description: "Piattaforma streaming eventi con sottotitoli automatici", status: "da realizzare", priority: "medium" }
      ]
    },
    uditiva: {
      "Audio Systems": [
        { id: 308, description: "Upgrade impianto loop magnetico in sala conferenze", status: "da realizzare", priority: "low" }
      ],
      "Communication Systems": []
    },
    cognitiva: {
      "Staff Training": [
        { id: 309, description: "Corso specializzato per accoglienza eventi inclusivi", status: "da realizzare", priority: "medium" }
      ],
      "Technology Access": [
        { id: 310, description: "Interfacce semplificate per sistemi audiovisivi", status: "da verificare", priority: "low" }
      ],
      "Guest Services": [
        { id: 311, description: "Servizio assistenza dedicata per eventi aziendali", status: "da realizzare", priority: "medium" }
      ]
    },
    alimentare: {
      "Food & Dining": [
        { id: 312, description: "Buffet accessibili con piatti ad altezza variabile", status: "da verificare", priority: "low" }
      ],
      "Recreation Areas": []
    }
  }
};

// Helper function to get module suggestions for a category
const getModuleSuggestionsForCategory = (structureId: string, categoryId: string) => {
  const categoryKey = categoryId === "alimentare" ? "alimentare" : categoryId;
  const moduleSuggestions = mockModuleSuggestions[structureId]?.[categoryKey] || {};

  // Normalize statuses for Structure Manager - convert disallowed statuses to "da realizzare"
  const allowedStatuses = ["da realizzare", "in corso"];
  const normalizedSuggestions = {};

  Object.entries(moduleSuggestions as Record<string, any[]>).forEach(([moduleName, suggestions]) => {
    normalizedSuggestions[moduleName] = (suggestions ?? []).map(suggestion => ({
      ...suggestion,
      status: allowedStatuses.includes(suggestion?.status) ? suggestion.status : "da realizzare",
    }));
  });


  return normalizedSuggestions;
};

interface StructureManagerDashboardProps {
  onLogout?: () => void;
  onGoToPassport?: (structureSlug?: string) => void;
  onGoToPassport2?: (structureSlug?: string) => void;
  onRequestVerification?: (suggestion: any, structureName: string, structureId?: string) => void;
  verificationsInProgress?: Set<string>;
  initialTab?: string;
  hideNavigation?: boolean;
  customBackHandler?: () => void;
  selectedStructureData?: any;
  user?: any;
  managedStructures?: any[];
  portals?: string[];
  cachedStructures?: any[];
  onGoHome?: () => void;
  onSwitchToPortal?: () => void;
}

export function StructureManagerDashboard({ onLogout, onGoToPassport, onGoToPassport2, onRequestVerification, verificationsInProgress, initialTab, hideNavigation, customBackHandler, selectedStructureData, user, managedStructures, portals, cachedStructures, onGoHome, onSwitchToPortal }: StructureManagerDashboardProps) {
  const initialSelectedId =
    typeof selectedStructureData === 'string'
      ? selectedStructureData
      : selectedStructureData?.id || "1";
  const [selectedStructureId, setSelectedStructureId] = useState(initialSelectedId);
  const [selectedStructureSlug, setSelectedStructureSlug] = useState<string | null>(null);
  const [structuresList, setStructuresList] = useState<Array<{ id?: string; slug: string; name: string; type?: string }>>([]);
  const [fetchedStructure, setFetchedStructure] = useState<any | null>(null);
  const [publicStructureDoc, setPublicStructureDoc] = useState<any | null>(null);
  const [loadingStructuresList, setLoadingStructuresList] = useState(false);
  const [noManagedMatch, setNoManagedMatch] = useState(false);
  const [structureLoadError, setStructureLoadError] = useState<string | null>(null);

  // Load structures list using local mapping (src/data) filtered to ONLY managed structures; fallback to public index only for other parts, not the header menu.
  useEffect(() => {
    let mounted = true;
    async function loadList() {
  // Console debug removed to keep logs clean
      setLoadingStructuresList(true);
      try {
        const items: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
        const managedArr: any[] = Array.isArray(managedStructures) ? managedStructures : [];
        const norm = (s: string) => String(s || '').trim().toLowerCase();
        const ensureJson = (s: string) => s.endsWith('.json') ? s : `${s}.json`;
        const resolveManagedIds = (arr: any[]) => {
          const ids: string[] = [];
          const bySlug = new Map<string, string>();
          for (const it of items) {
            if (!it?.id) continue;
            const id = String(it.id);
            bySlug.set(norm(id), id);
            if (it.slug) {
              const slug = String(it.slug);
              bySlug.set(norm(slug), id);
              bySlug.set(norm(ensureJson(slug)), id);
            }
          }
          for (const x of arr) {
            if (typeof x === 'string') {
              const key = norm(x);
              const mapped = bySlug.get(key) || bySlug.get(norm(ensureJson(x)));
              ids.push(mapped || String(x));
            } else if (x && typeof x === 'object' && x.id) {
              ids.push(String(x.id));
            }
          }
          return Array.from(new Set(ids.filter(Boolean)));
        };
        const resolveSlugForId = (id: string) => {
          const match = items.find((it: any) => String(it.id) === String(id));
          return match?.slug ? String(match.slug) : null;
        };

        const candidateIds = resolveManagedIds(managedArr);
        const cacheMap: Record<string, any> = Object.fromEntries(
          (cachedStructures || []).map((d: any) => [String(d.id), d])
        );
        const cachedDocs = candidateIds.map((id) => cacheMap[String(id)]).filter(Boolean);
        const missingIds = candidateIds.filter((id) => !cacheMap[String(id)]);
        const fetchedDocs = missingIds.length ? await getStructuresByIds(missingIds) : [];
        const allDocs = [...cachedDocs, ...(Array.isArray(fetchedDocs) ? fetchedDocs : [])];
        const list = allDocs.map((doc: any) => {
          const slug = resolveSlugForId(String(doc.id)) || String(doc.id);
          const name = doc?.structure?.accommodation_data?.accommodation_name || doc?.name || String(doc.id);
          return { id: String(doc.id), slug, name, type: doc?.structure?.accommodation_data?.type ?? '' };
        });
        if (mounted) setStructuresList(list);
        setNoManagedMatch(list.length === 0);

        if (mounted && !selectedStructureSlug && !selectedStructureData && list.length > 0) {
          handleStructureSelectBySlug(list[0].id || list[0].slug);
        }
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setLoadingStructuresList(false);
      }
    }
    loadList();
    return () => { mounted = false; };
  }, [managedStructures]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState(initialTab || "monitor");
  const [selectedImprovementCategory, setSelectedImprovementCategory] = useState("motoria");
  const prevActiveTabRef = useRef(activeTab);
  const improveEntryRef = useRef<'monitor' | 'navbar' | null>(null);

  // Widget customization state
  const [widgetSize, setWidgetSize] = useState("standard");
  const [colorTheme, setColorTheme] = useState("blue");
  const [language, setLanguage] = useState("it");
  const [widgetShareTab, setWidgetShareTab] = useState("widget");

  // Manage suggestion dialog state
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);
  const [newStatus, setNewStatus] = useState("");
  const [isSavingSuggestions, setIsSavingSuggestions] = useState(false);

  const currentStructurePrivate = React.useMemo(() => {
    if (fetchedStructure) return fetchedStructure;
    if (selectedStructureData && typeof selectedStructureData === 'object') return selectedStructureData;
    return null;
  }, [fetchedStructure, selectedStructureData]);

  useEffect(() => {
    let active = true;

    const loadPublicStructure = async () => {
      try {
        if (selectedStructureSlug) {
          const cleanSlug = String(selectedStructureSlug).replace(/\.json$/i, '');
          const direct = await getPublicStructureBySlug(cleanSlug);
          if (active) setPublicStructureDoc(direct || null);
          if (direct) return;
        }

        const allPublic = await getAllPublicStructures();
        const match = allPublic.find((structure: any) => {
          const backendId = structure?.id_backend || structure?.backend_id || structure?.structureId || '';
          return String(backendId) === String(selectedStructureId);
        });
        if (active) setPublicStructureDoc(match || null);
      } catch {
        if (active) setPublicStructureDoc(null);
      }
    };

    if (selectedStructureId || selectedStructureSlug) {
      loadPublicStructure();
    } else {
      setPublicStructureDoc(null);
    }

    return () => {
      active = false;
    };
  }, [selectedStructureId, selectedStructureSlug]);

  const pdfStructure = React.useMemo(() => {
    const modules = Array.isArray(currentStructurePrivate?.modules)
      ? currentStructurePrivate.modules
      : [];
    const mappedModules = modules.map((mod: any) => ({
      name: String(mod?.name || mod?.module_name || mod?.type || 'Modulo'),
      type: String(mod?.type || mod?.name || mod?.module_name || 'Modulo'),
      content: (mod?.content || mod?.questions || {}) as Record<string, string>,
    }));

    return {
      id_backend: String(
        currentStructurePrivate?.id_backend ||
          currentStructurePrivate?.backend_id ||
          currentStructurePrivate?.structureId ||
          currentStructurePrivate?.id ||
          ''
      ),
      lastModified:
        currentStructurePrivate?.lastModified ||
        currentStructurePrivate?.last_modified ||
        currentStructurePrivate?.updated_at ||
        currentStructurePrivate?.updatedAt,
      public_infos: currentStructurePrivate?.public_infos,
      modules: mappedModules,
    };
  }, [currentStructurePrivate]);

  const buildAccessibilityData = (scores: any, requirementsByCategory?: Record<string, { completed: number; missing: number }>) => {
    const getRequirements = (categoryKey: string) => {
      const defaults = { completed: 0, missing: 0 };
      if (!requirementsByCategory) return defaults;
      return requirementsByCategory[categoryKey] || defaults;
    };

    return [
      { category: 'Motoria', score: Math.round(scores?.motoria || 0), color: '#0369a1', requirements: getRequirements('motoria') },
      { category: 'Visiva', score: Math.round(scores?.visiva || scores?.sensoriale || 0), color: '#ea580c', requirements: getRequirements('visiva') },
      { category: 'Uditiva', score: Math.round(scores?.uditiva || scores?.udito || 0), color: '#7c3aed', requirements: getRequirements('uditiva') },
      { category: 'Cognitiva', score: Math.round(scores?.cognitiva || 0), color: '#0891b2', requirements: getRequirements('cognitiva') },
      { category: 'Alimentare', score: Math.round(scores?.alimentare || 0), color: '#059669', requirements: getRequirements('alimentare') },
    ];
  };

  const privateAccessibilityData = React.useMemo(() => {
    if (!currentStructurePrivate) return null;
    const scores = extractAccessibilityScoresFromStructure(currentStructurePrivate);
    return buildAccessibilityData(scores);
  }, [currentStructurePrivate]);

  // Get accessibility data for current structure
  const [monitorAccessData, setMonitorAccessData] = useState<any[] | null>(null);
  
  // Use hooks for V2 suggestions
  const legacyData = fetchedStructure?.suggestions || currentStructurePrivate?.suggestions;
  const { data: suggestionsResult, loading: suggestionsLoading } = useLegacySuggestions(selectedStructureId, legacyData);
  
  const { 
    data: suggestionsV2Summary, 
    loading: suggestionsV2SummaryLoading 
  } = useStructureSuggestionsSummary(activeTab === 'monitor' ? selectedStructureId : null);
  
  const { 
    data: suggestionsV2Fetched, 
    loading: suggestionsV2Loading,
    error: suggestionsV2Error 
  } = useStructureSuggestionsItems(activeTab === 'improve' ? selectedStructureId : null);
  
  // Local state for suggestionsV2 to enable optimistic updates during status changes
  const [suggestionsV2, setSuggestionsV2] = useState<SuggestionsV2 | null>(null);
  
  // Sync fetched data to local state
  useEffect(() => {
    if (suggestionsV2Fetched) {
      setSuggestionsV2(suggestionsV2Fetched);
    }
  }, [suggestionsV2Fetched]);
  const [improveSearchQuery, setImproveSearchQuery] = useState('');
  const [improveStatusFilter, setImproveStatusFilter] = useState<'all' | SuggestionStatus>('all');
  const [improveViewMode, setImproveViewMode] = useState<'category' | 'status'>('category');
  const [improveCategoryFilter, setImproveCategoryFilter] = useState<string>('all');

  useEffect(() => {
    if (activeTab !== 'improve') {
      prevActiveTabRef.current = activeTab;
      return;
    }

    if (improveEntryRef.current === 'navbar') {
      setImproveViewMode('status');
      setImproveStatusFilter('todo');
      setImproveSearchQuery('');
      setImproveCategoryFilter('all');
      setOpenAccordionItems(['todo']);
    } else if (improveEntryRef.current === 'monitor') {
      setImproveViewMode('category');
    }

    improveEntryRef.current = null;
    prevActiveTabRef.current = activeTab;
  }, [activeTab]);
  const [v2UpdatingItems, setV2UpdatingItems] = useState<Record<string, boolean>>({});
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [bulkActionDialog, setBulkActionDialog] = useState<{ open: boolean; action: SuggestionStatus | null }>({ open: false, action: null });
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  // Prefer fresh Firestore-derived scores in Monitor view; fall back to private snapshot
  const currentAccessibilityData = monitorAccessData || privateAccessibilityData || [];

  const isSuggestionsV2Enabled = getSuggestionsV2Flag();

  // Auto-open accordion when navigating from "Apri Migliora" button
  useEffect(() => {
    if (improveCategoryFilter && improveCategoryFilter !== 'all' && activeTab === 'improve') {
      setOpenAccordionItems((prev) => {
        // Add category to open items if not already there
        if (!prev.includes(improveCategoryFilter)) {
          return [...prev, improveCategoryFilter];
        }
        return prev;
      });
    }
  }, [improveCategoryFilter, activeTab]);

  // Define passport navigation items
  const passportNavItems = [
    { id: "general", label: "Informazioni generali", icon: Building2 },
    { id: "accessibility", label: "Informazioni sull'accessibilità", icon: CheckCircle },
    { id: "public-transport", label: "Trasporto pubblico", icon: Bus },
    { id: "parking", label: "Parcheggio", icon: Car },
    { id: "entrance", label: "Ingresso", icon: DoorOpen },
    { id: "interior-navigation", label: "Orientamento interno", icon: Navigation },
    { id: "restrooms-facilities", label: "Servizi igienici e servizi", icon: Home },
    { id: "elevators-lifts", label: "Ascensori e montacarichi", icon: ArrowLeft },
    { id: "safety-emergency", label: "Sicurezza ed emergenze", icon: Shield },
    { id: "audio-systems", label: "Sistemi audio", icon: Volume2 },
    { id: "visual-aids", label: "Ausili visivi", icon: Eye },
    { id: "wayfinding-signage", label: "Orientamento e segnaletica", icon: Navigation },
    { id: "staff-training", label: "Formazione del personale", icon: Users },
    { id: "communication-systems", label: "Sistemi di comunicazione", icon: Megaphone },
    { id: "alimentare-dining", label: "Ristorazione", icon: Utensils },
    { id: "recreation-areas", label: "Aree ricreative", icon: Gamepad2 },
    { id: "technology-access", label: "Accesso alla tecnologia", icon: WifiIcon },
    { id: "digital-accessibility", label: "Accessibilità digitale", icon: Monitor },
    { id: "maintenance-upkeep", label: "Manutenzione e cura", icon: Wrench },
    { id: "guest-services", label: "Servizi agli ospiti", icon: HeadphonesIcon },
    { id: "compliance-documentation", label: "Documentazione di conformità", icon: FileText },
    { id: "accessibility-resources", label: "Risorse per l'accessibilità", icon: BookOpen }
  ];

  const handleStructureSelect = (structureId: string) => {
    setSelectedStructureId(structureId);
  };

  // Accepts either a slug or an ID; resolves via mapping and loads ONLY from Firestore
  const handleStructureSelectBySlug = async (slugOrId: string) => {
    const items: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
    const byId = items.find((it: any) => String(it.id) === String(slugOrId));
    const bySlug = byId ? byId : items.find((it: any) => String(it.slug) === String(slugOrId));
    const resolvedId = byId ? String(byId.id) : (bySlug?.id ? String(bySlug.id) : null);
    const resolvedSlug = bySlug?.slug ? String(bySlug.slug) : (byId?.slug ? String(byId.slug) : null);

  // Console debug removed to keep logs clean

    try {
      setStructureLoadError(null);
      if (!resolvedId) {
        setStructureLoadError('Struttura non disponibile');
        return;
      }

      let doc: any | null = null;
      if (Array.isArray(cachedStructures) && cachedStructures.length) {
        const cached = cachedStructures.find((d: any) => String(d?.id) === resolvedId);
        if (cached) doc = cached;
      }
      if (!doc) {
        const docs = await getStructuresByIds([resolvedId]);
        doc = docs?.[0] || null;
      }
      if (!doc) {
        setStructureLoadError('Struttura non disponibile');
        return;
      }

      setFetchedStructure(doc);
      setSelectedStructureId(resolvedId);
      setSelectedStructureSlug(resolvedSlug || resolvedId);
    } catch (err) {
  // Console debug removed to keep logs clean
      setStructureLoadError('Struttura non disponibile');
    }
  };

  // Initialize from selectedStructureData coming from parent (App) if available
  useEffect(() => {
    const data: any = selectedStructureData;
    if (!data) return;
      // If a full doc was passed
      if (typeof data === 'object' && data.id && !fetchedStructure) {
      setFetchedStructure(data);
      setSelectedStructureId(String(data.id));
      setStructureLoadError(null);
      const items: any[] = Array.isArray((idSlugData as any)?.items) ? (idSlugData as any).items : [];
      const m = items.find((it: any) => String(it.id) === String(data.id));
      if (m?.slug) setSelectedStructureSlug(String(m.slug));
      else setSelectedStructureSlug(String(data.id));
      return;
    }
    // If a string was passed (id or slug), set it directly and try to resolve
    if (typeof data === 'string') {
      setSelectedStructureId(String(data));
      setSelectedStructureSlug(String(data));
      handleStructureSelectBySlug(data);
    }
  }, [selectedStructureData]);

  // Accessibility categories for improvement section
  const accessibilityCategories = [
    { id: "motoria", label: "Motoria", icon: Accessibility, score: currentAccessibilityData?.find(item => item.category === "Motoria")?.score || 0 },
    { id: "visiva", label: "Visiva", icon: Eye, score: currentAccessibilityData?.find(item => item.category === "Visiva")?.score || 0 },
    { id: "uditiva", label: "Uditiva", icon: Volume2, score: currentAccessibilityData?.find(item => item.category === "Uditiva")?.score || 0 },
    { id: "cognitiva", label: "Cognitiva", icon: Users, score: currentAccessibilityData?.find(item => item.category === "Cognitiva")?.score || 0 },
    { id: "alimentare", label: "Alimentare", icon: Utensils, score: currentAccessibilityData?.find(item => item.category === "Alimentare")?.score || 0 },
  ];

  const privateSuggestions = React.useMemo(() => (
    Array.isArray(suggestionsResult?.suggestions) ? suggestionsResult?.suggestions : []
  ), [suggestionsResult, suggestionsLoading]);

  const normalizedStatusFilter = statusFilter === 'not_started' ? '' : statusFilter;
  const filteredSuggestions = privateSuggestions.filter((suggestion: any) =>
    normalizedStatusFilter === "all" || suggestion.status === normalizedStatusFilter
  );

  const portalNameFromStructure = currentStructurePrivate?.structure?.portal_administrators?.portal_name || "";
  const normalizePortalName = (value: unknown) => String(value ?? "").trim().toLowerCase();
  const currentStructurePortalName = currentStructurePrivate?.structure?.portal_administrators?.portal_name || "";
  const isCurrentStructureVisitPortal = normalizePortalName(currentStructurePortalName).includes("bergamo");
  const hasVisitInCached = Array.isArray(cachedStructures) && cachedStructures.some((structure: any) =>
    normalizePortalName(structure?.structure?.portal_administrators?.portal_name).includes("bergamo")
  );
  const hasVisitInManaged = Array.isArray(managedStructures) && managedStructures.some((entry: any) =>
    normalizePortalName(
      entry?.portalName ??
      entry?.portal ??
      entry?.portal_name ??
      entry?.portal_administrators?.portal_name
    ).includes("bergamo")
  );
  const hasUserPortals = Array.isArray(portals) && portals.length > 0;
  const isVisitByUserPortals = getPortalBranding(Array.isArray(portals) ? portals : []) === "visit-bergamo";
  const isVisitBergamo = hasUserPortals
    ? isVisitByUserPortals
    : (hasVisitInCached || hasVisitInManaged || normalizePortalName(portalNameFromStructure).includes("bergamo"));

  const normalizeText = (value: unknown) => String(value ?? '').trim().toLowerCase();
  const matchesSelectedCategory = (suggestion: any) => {
    if (selectedImprovementCategory === 'all') return true;
    const raw = normalizeText(
      suggestion?.category ??
      suggestion?.categoria ??
      suggestion?.accessibilityCategory ??
      suggestion?.area ?? ''
    );
    if (!raw) return true;
    return raw.includes(selectedImprovementCategory);
  };

  const improvementModules = React.useMemo(() => {
    const grouped = new Map<string, any[]>();
    privateSuggestions.forEach((suggestion: any, index: number) => {
      if (!matchesSelectedCategory(suggestion)) return;
      const moduleName = String(
        suggestion?.module ||
        suggestion?.moduleName ||
        suggestion?.category ||
        suggestion?.categoria ||
        'Suggerimenti'
      );
      const description = typeof suggestion?.description === 'string'
        ? suggestion.description
        : Array.isArray(suggestion?.interventi)
          ? suggestion.interventi.join(' ')
          : String(suggestion?.interventi || suggestion?.title || suggestion?.name || '').trim();
      if (!description) return;
      const suggestionId = suggestion?.id ?? suggestion?._id ?? `${index}`;
      const structureId = selectedStructureId;
      const suggestionKey = `${structureId}:${suggestionId}`;
      const isInVerification = verificationsInProgress?.has(suggestionKey);
      
      const normalized = {
        id: suggestionId,
        description,
        status: isInVerification ? 'verifica_in_corso' : (suggestion?.status || ''),
        priority: suggestion?.priority || suggestion?.priorita || suggestion?.priority_level,
      };
      if (!grouped.has(moduleName)) grouped.set(moduleName, []);
      grouped.get(moduleName)!.push(normalized);
    });
    return Array.from(grouped.entries()).map(([moduleName, suggestions]) => ({ moduleName, suggestions }));
  }, [privateSuggestions, selectedImprovementCategory, verificationsInProgress, selectedStructureId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'completo':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'in corso':
        return 'bg-blue-100 text-blue-800';
      case 'not_started':
      case '':
      case 'da realizzare':
        return 'bg-yellow-100 text-yellow-800';
      case 'da verificare': return 'bg-orange-100 text-orange-800';
      case 'pianificato': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getV2StatusLabel = (status: SuggestionStatus) => {
    switch (status) {
      case 'todo':
        return 'Da fare';
      case 'in_progress':
        return 'In corso';
      case 'verifica_in_corso':
        return 'Verifica in corso';
      case 'done':
        return 'Completato';
      case 'rejected':
        return 'Scartato';
      default:
        return 'Da fare';
    }
  };

  const getV2StatusBadgeClass = (status: SuggestionStatus) => {
    switch (status) {
      case 'todo':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'verifica_in_corso':
        return 'bg-orange-100 text-orange-800';
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getSourceBadge = (source: 'collection' | 'legacy_structure' | 'none') => {
    switch (source) {
      case 'collection':
        return <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700 text-ss">V2</Badge>;
      case 'legacy_structure':
        return <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700 text-ss">Legacy</Badge>;
      case 'none':
        return <Badge variant="outline" className="bg-gray-50 border-gray-200 text-gray-700 text-ss">No suggestions</Badge>;
      default:
        return null;
    }
  };

  const getDominantStatus = (items: { status: SuggestionStatus }[]) => {
    const counts: Record<SuggestionStatus, number> = {
      todo: 0,
      in_progress: 0,
      verifica_in_corso: 0,
      done: 0,
      rejected: 0,
    };
    items.forEach((item) => {
      counts[item.status] += 1;
    });
    const order: SuggestionStatus[] = ['done', 'in_progress', 'todo', 'rejected'];
    let best = order[0];
    order.forEach((status) => {
      if (counts[status] > counts[best]) best = status;
      if (counts[status] === counts[best]) {
        best = order.indexOf(status) < order.indexOf(best) ? status : best;
      }
    });
    return best;
  };

  const normalizeSearchQuery = (value: string) => value.trim().toLowerCase();
  const matchesSearch = (text: string, query: string) => {
    if (!query) return true;
    return text.toLowerCase().includes(query);
  };

  // Removed old suggestionsV2Summary memo - now using server-provided summary from suggestionsV2Summary state

  const updateV2ItemStatus = async (itemId: string, status: SuggestionStatus) => {
    if (!selectedStructureId || !suggestionsV2) return;
    if (v2UpdatingItems[itemId]) return;

    const previousStatus = suggestionsV2.categories
      .flatMap((category) => category.items)
      .find((item) => item.id === itemId)?.status;
    const updatedCategories = suggestionsV2.categories.map((category) => ({
      ...category,
      items: category.items.map((item) =>
        item.id === itemId ? { ...item, status } : item
      ),
    }));

    setSuggestionsV2({ ...suggestionsV2, categories: updatedCategories });
    setV2UpdatingItems((prev) => ({ ...prev, [itemId]: true }));

    try {
      await updateSuggestionStatusSafe({
        structureId: selectedStructureId,
        suggestionId: itemId,
        newStatus: status,
        prevStatus: previousStatus,
        source: 'single',
      });
    } finally {
      setV2UpdatingItems((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const updateV2ItemsStatusBulk = async (itemIds: string[], status: SuggestionStatus) => {
    if (!selectedStructureId || !suggestionsV2 || itemIds.length === 0) return;
    if (isBulkUpdating) return;

    const updatedCategories = suggestionsV2.categories.map((category) => ({
      ...category,
      items: category.items.map((item) =>
        itemIds.includes(item.id) ? { ...item, status } : item
      ),
    }));

    setSuggestionsV2({ ...suggestionsV2, categories: updatedCategories });
    setIsBulkUpdating(true);

    try {
      const statusByItemId = itemIds.reduce((acc, id) => {
        acc[id] = status;
        return acc;
      }, {} as Record<string, SuggestionStatus>);

      const result = await updateSuggestionStatusesSafe({
        structureId: selectedStructureId,
        statusByItemId,
        source: 'bulk',
      });
      if (result.remote) {
        toast.success(`${itemIds.length} interventi aggiornati con successo`);
      }
      setSelectedItemIds(new Set());
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkAction = (status: SuggestionStatus) => {
    const count = selectedItemIds.size;
    if (count === 0) return;
    
    // Show confirmation for large operations (>20 items)
    if (count > 20) {
      setBulkActionDialog({ open: true, action: status });
    } else {
      updateV2ItemsStatusBulk(Array.from(selectedItemIds), status);
    }
  };

  const confirmBulkAction = () => {
    if (bulkActionDialog.action) {
      updateV2ItemsStatusBulk(Array.from(selectedItemIds), bulkActionDialog.action);
    }
    setBulkActionDialog({ open: false, action: null });
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const toggleSelectAll = (itemIds: string[]) => {
    if (itemIds.length === 0) return;
    const allSelected = itemIds.every((id) => selectedItemIds.has(id));
    if (allSelected) {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        itemIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        itemIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };



  // Helper functions for widget customization
  const getWidgetStyles = (theme: string) => {
    switch (theme) {
      case 'orange':
        return {
          gradient: 'bg-gradient-to-br from-orange-500 to-orange-600',
          primary: 'text-orange-600',
          primaryBg: 'bg-orange-500',
          accent: 'bg-orange-100 text-orange-600',
          border: 'border-orange-600'
        };
      case 'brand':
        return {
          gradient: 'bg-gradient-travelin',
          primary: 'text-travelin-blue',
          primaryBg: 'bg-gradient-travelin',
          accent: 'bg-blue-100 text-travelin-blue',
          border: 'border-travelin-blue'
        };
      default: // blue
        return {
          gradient: 'bg-gradient-travelin',
          primary: 'text-travelin-blue',
          primaryBg: 'bg-gradient-travelin',
          accent: 'bg-blue-100 text-travelin-blue',
          border: 'border-travelin-blue'
        };
    }
  };

  // Filter categories that need urgent improvement (below 75%)
  const urgentImprovementCategories = currentAccessibilityData?.filter(item => item.score < 75) || [];

  const getWidgetText = (lang: string) => {
    const texts = {
      it: {
        title: 'Passaporto Accessibilità',
        digitalTitle: 'Passaporto Digitale di Accessibilità',
        available: 'Passaporto Accessibilità Disponibile',
        complete: 'Informazioni complete sull\'accessibilità',
        documentation: 'Documentazione completa sull\'accessibilità',
        description: 'Documentazione completa dell\'accessibilità disponibile. Questo passaporto fornisce informazioni dettagliate su caratteristiche, servizi e strutture di accessibilità per tutti i visitatori.',
        viewButton: 'Visualizza Passaporto',
        viewComplete: 'Visualizza Passaporto Completo',
        verified: 'Verificato',
        poweredBy: 'Powered by Travelin',
        certified: 'Certificato da Travelin • Ultimo aggiornamento 2 giorni fa',
        contact: 'Contatta'
      },
      en: {
        title: 'Accessibility Passport',
        digitalTitle: 'Digital Accessibility Passport',
        available: 'Accessibility Passport Available',
        complete: 'Complete accessibility information',
        documentation: 'Complete accessibility documentation',
        description: 'Comprehensive accessibility documentation available. This passport provides detailed information about accessibility features, services, and facilities for all visitors.',
        viewButton: 'View Passport',
        viewComplete: 'View Complete Passport',
        verified: 'Verified',
        poweredBy: 'Powered by Travelin',
        certified: 'Certified by Travelin • Last updated 2 days ago',
        contact: 'Contact'
      },
      de: {
        title: 'Barrierefreiheits-Pass',
        digitalTitle: 'Digitaler Barrierefreiheits-Pass',
        available: 'Barrierefreiheits-Pass Verfügbar',
        complete: 'Vollständige Barrierefreiheitsinformationen',
        documentation: 'Vollständige Barrierefreiheitsdokumentation',
        description: 'Umfassende Barrierefreiheitsdokumentation verfügbar. Dieser Pass bietet detaillierte Informationen über Barrierefreiheitsmerkmale, Services und Einrichtungen für alle Besucher.',
        viewButton: 'Pass Anzeigen',
        viewComplete: 'Vollständigen Pass Anzeigen',
        verified: 'Verifiziert',
        poweredBy: 'Powered by Travelin',
        certified: 'Zertifiziert von Travelin • Zuletzt aktualisiert vor 2 Tagen',
        contact: 'Kontakt'
      },
      fr: {
        title: 'Passeport d\'Accessibilité',
        digitalTitle: 'Passeport Numérique d\'Accessibilité',
        available: 'Passeport d\'Accessibilité Disponible',
        complete: 'Informations complètes sur l\'accessibilité',
        documentation: 'Documentation complète sur l\'accessibilité',
        description: 'Documentation complète sur l\'accessibilité disponible. Ce passeport fournit des informations détaillées sur les caractéristiques, services et installations d\'accessibilité pour tous les visiteurs.',
        viewButton: 'Voir le Passeport',
        viewComplete: 'Voir le Passeport Complet',
        verified: 'Vérifié',
        poweredBy: 'Propulsé par Travelin',
        certified: 'Certifié par Travelin • Dernière mise à jour il y a 2 jours',
        contact: 'Contact'
      }
    };
    return texts[lang as keyof typeof texts] || texts.it;
  };

  const getWidgetDimensions = (size: string) => {
    switch (size) {
      case 'compact':
        return {
          containerClass: 'max-w-xs p-3',
          iconSize: 'w-8 h-8',
          titleSize: 'text-m',
          padding: 'p-3'
        };
      case 'detailed':
        return {
          containerClass: 'max-w-xl p-8',
          iconSize: 'w-16 h-16',
          titleSize: 'text-xxl',
          padding: 'p-8'
        };
      default: // standard
        return {
          containerClass: 'max-w-md p-5',
          iconSize: 'w-12 h-12',
          titleSize: 'text-xl',
          padding: 'p-5'
        };
    }
  };

  const CATEGORY_KEYS = ['motoria', 'visiva', 'uditiva', 'cognitiva', 'alimentare'] as const;
  type StatusCategory = typeof CATEGORY_KEYS[number];
  type StatusValue = '' | 'in_progress' | 'completed';
  type StatusByCategory = Record<StatusCategory, StatusValue>;

  const toBackendStatus = (status: unknown) => {
    const raw = String(status ?? '').trim().toLowerCase();
    if (!raw) return '';
    if (raw === 'not_started') return '';
    if (raw === 'in_progress' || raw === 'in progress' || raw === 'in corso') return 'in_progress';
    if (raw === 'completed' || raw === 'completo' || raw === 'completato') return 'completed';
    if (raw === 'da realizzare') return '';
    return '';
  };

  const getCategoryKey = (suggestion: any): StatusCategory | null => {
    const raw = normalizeText(
      suggestion?.category ??
      suggestion?.categoria ??
      suggestion?.accessibilityCategory ??
      suggestion?.area ?? ''
    );
    if (!raw) return null;
    if (raw.includes('motor')) return 'motoria';
    if (raw.includes('visiv') || raw.includes('sensor')) return 'visiva';
    if (raw.includes('udit') || raw.includes('audio')) return 'uditiva';
    if (raw.includes('cognitiv')) return 'cognitiva';
    if (raw.includes('aliment')) return 'alimentare';
    return null;
  };

  // Calculate requirements by category from suggestions (for Monitor KPIs)
  const requirementsByCategory = React.useMemo(() => {
    const requirements: Record<string, { completed: number; missing: number; total: number }> = {
      motoria: { completed: 0, missing: 0, total: 0 },
      visiva: { completed: 0, missing: 0, total: 0 },
      uditiva: { completed: 0, missing: 0, total: 0 },
      cognitiva: { completed: 0, missing: 0, total: 0 },
      alimentare: { completed: 0, missing: 0, total: 0 },
    };

    privateSuggestions.forEach((suggestion: any) => {
      const categoryKey = getCategoryKey(suggestion);
      if (!categoryKey || !(categoryKey in requirements)) return;

      requirements[categoryKey].total++;

      const status = toBackendStatus(suggestion?.status);
      if (status === 'completed') {
        requirements[categoryKey].completed++;
      } else {
        // Not started or in progress count as "missing" (not yet completed)
        requirements[categoryKey].missing++;
      }
    });

    return requirements;
  }, [privateSuggestions]);

  // When entering Monitor tab or switching structure, fetch a fresh private doc once.
  useEffect(() => {
    let mounted = true;
    async function refreshPrivateStructure() {
      try {
        if (activeTab !== 'monitor') {
          setMonitorAccessData(null);
          return;
        }

        const id = selectedStructureId;
        if (!id) {
          setMonitorAccessData(null);
          return;
        }

        const docs = await getStructuresByIds([id]);
        const doc = Array.isArray(docs) ? docs[0] : null;
        if (!mounted) return;

        if (doc) {
          setFetchedStructure(doc);
        } else {
          setMonitorAccessData(null);
        }
      } catch {
        if (mounted) setMonitorAccessData(null);
      }
    }

    refreshPrivateStructure();
    return () => { mounted = false; };
  }, [activeTab, selectedStructureId]);

  useEffect(() => {
    if (activeTab !== 'monitor') {
      setMonitorAccessData(null);
      return;
    }

    if (!currentStructurePrivate) {
      setMonitorAccessData(null);
      return;
    }

    const scores = extractAccessibilityScoresFromStructure(currentStructurePrivate);
    setMonitorAccessData(buildAccessibilityData(scores, requirementsByCategory));
  }, [activeTab, currentStructurePrivate, requirementsByCategory]);

  const buildStatusByCategory = (suggestions: any[], override?: { categoryKey: StatusCategory | null; status: string }): StatusByCategory => {
    const out: StatusByCategory = {
      motoria: '',
      visiva: '',
      uditiva: '',
      cognitiva: '',
      alimentare: '',
    };
    suggestions.forEach((suggestion) => {
      const key = getCategoryKey(suggestion);
      if (key && CATEGORY_KEYS.includes(key) && !out[key]) {
        out[key] = toBackendStatus(suggestion?.status) as StatusValue;
      }
    });
    if (override?.categoryKey && CATEGORY_KEYS.includes(override.categoryKey)) {
      out[override.categoryKey] = toBackendStatus(override.status) as StatusValue;
    }
    return out;
  };

  const mapStatusToMessage = (status: number) => {
    if (status === 401 || status === 403) return 'Permessi insufficienti';
    if (status === 404) return 'Struttura non trovata';
    return 'Errore durante il salvataggio';
  };

  const saveSuggestionsStatus = async (
    structureId: string,
    statusByCategory: StatusByCategory,
    token: string
  ) => {
    const res = await fetch(`/nextapi/structures/${structureId}/suggestions/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ statusByCategory }),
    });

    if (!res.ok) {
      let detailMessage = '';
      try {
        const json = await res.json();
        detailMessage = String(json?.message || json?.error || '').trim();
      } catch {
        try {
          detailMessage = String(await res.text()).trim();
        } catch {
          detailMessage = '';
        }
      }
      const baseMessage = mapStatusToMessage(res.status);
      const message = detailMessage ? `${baseMessage} (${detailMessage})` : baseMessage;
      const err = new Error(message) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }

    return res.json();
  };

  const handleManageSuggestion = (suggestion: any) => {
    setSelectedSuggestion(suggestion);
    const normalized = toBackendStatus(suggestion?.status);
    setNewStatus(normalized || 'not_started');
    setIsManageDialogOpen(true);
  };

  const handleSaveStatus = async () => {
    if (!selectedSuggestion || !selectedStructureId) return;
    if (isSavingSuggestions) return;
    const user = auth.currentUser;
    if (!user) {
      toast.error('Permessi insufficienti');
      return;
    }

    setIsSavingSuggestions(true);
    try {
      const token = await user.getIdToken();
      const categoryKey = getCategoryKey(selectedSuggestion);
      const statusByCategory: StatusByCategory = buildStatusByCategory(privateSuggestions, {
        categoryKey,
        status: newStatus,
      });
      const result = await saveSuggestionsStatus(selectedStructureId, statusByCategory, token);
      if (result?.suggestions && typeof result === 'object') {
        setFetchedStructure((prev) => (prev ? { ...prev, suggestions: result.suggestions } : prev));
      }
      toast.success('Salvato');
      setIsManageDialogOpen(false);
      setSelectedSuggestion(null);
    } catch (err: any) {
      const status = err?.status;
      if (status === 401 || status === 403 || status === 404) {
        toast.error(err?.message || mapStatusToMessage(status));
      } else {
        toast.error(err?.message || 'Errore durante il salvataggio');
        console.error('Save suggestions status failed', err);
      }
    } finally {
      setIsSavingSuggestions(false);
    }
  };

  // Helper function to get structure name prioritizing "NOME STRUTTURA" (6.1) from "Dati info azienda"
  const getStructureName = (structure: any) => {
    // Helper to extract value from either a string or an object with .value property
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && val.value !== undefined) {
        return String(val.value);
      }
      return String(val);
    };

    // PRIORITIZE "NOME STRUTTURA" (6.1) from "Dati info azienda" module
    if (Array.isArray(structure?.modules) && structure.modules.length > 0) {
      const firstModule = structure.modules[0];
      if (firstModule?.content) {
        // Try both numbered key (6.1) and human-readable key (NOME STRUTTURA)
        let nomeStruttura = extractValue(firstModule.content["6.1"]);
        if (!nomeStruttura) {
          nomeStruttura = extractValue(firstModule.content["NOME STRUTTURA"]);
        }
        if (nomeStruttura && nomeStruttura.trim()) {
          return nomeStruttura;
        }
      }
    }
    
    // Fallback to standard accommodation_name
    return structure?.structure?.accommodation_data?.accommodation_name || structure?.name || '';
  };

  const handleRequestVerification = (suggestion: any) => {
    if (onRequestVerification) {
      const resolvedStructureId = currentStructurePrivate?.id
        ? String(currentStructurePrivate.id)
        : String(selectedStructureId || '');
      onRequestVerification(suggestion, getStructureName(currentStructurePrivate), resolvedStructureId || undefined);
    }
  };

  return (
    <div className={`min-h-screen ${isVisitBergamo ? "bg-[#f5f5f5]" : "bg-background"}`}>
     {/*  {(!isVisitBergamo) && !isCurrentStructureVisitPortal && structuresList.length > 0 &&
  currentStructurePortalName && (
    <StructureAttributionBar
      portalName={currentStructurePortalName}
      branding="default"
    />
  )} */}


      {/* Header */}
      <StructureHeader
        activeTab={activeTab as any}
        setActiveTab={(tab: any) => {
          if (tab === 'improve') {
            improveEntryRef.current = 'navbar';
            setImproveViewMode('status');
            setImproveStatusFilter('todo');
            setImproveSearchQuery('');
            setImproveCategoryFilter('all');
            setOpenAccordionItems(['todo']);
          }
          setActiveTab(tab);
        }}
        structureName={noManagedMatch ? '' : getStructureName(currentStructurePrivate)}
        user={user}
        onLogout={onLogout}
        onGoHome={onGoHome}
        structuresList={structuresList as any}
        selectedStructureSlug={selectedStructureSlug}
        onSwitchStructure={(slugOrId) => handleStructureSelectBySlug(slugOrId)}
        onSwitchToPortal={onSwitchToPortal}
        branding={isVisitBergamo ? "visit-bergamo" : "default"}
      />

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          
          {/* Error state when no managed structures matched any available structure */}
          {!loadingStructuresList && noManagedMatch && structuresList.length === 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">Nessuna struttura gestita trovata</CardTitle>
                <CardDescription className="text-red-600">
                  Non è stata trovata alcuna struttura gestita nel tuo profilo che coincida con le strutture disponibili.
                  Contatta il supporto del portale per associare il tuo account alla struttura corretta.
                </CardDescription>
              </CardHeader>
            </Card>
          )}
          {structureLoadError && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">Struttura non disponibile</CardTitle>
                <CardDescription className="text-red-600">
                  La struttura selezionata non e' disponibile o non e' accessibile con il tuo account.
                </CardDescription>
              </CardHeader>
            </Card>
          )}
          {structuresList.length > 0 && activeTab === "declare" && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">Il tuo Accessibility Passport</h2>
                <p className="text-s text-muted-foreground">
                  Qui puoi visualizzare il profilo di accessibilità della tua struttura e generare gli strumenti per condividerlo con i tuoi ospiti sui tuoi canali digitali.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap items-stretch gap-6">
              <Card className="flex-1 min-w-[320px] h-full">
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    <CardTitle>Cos'è l’Accessibility Passport</CardTitle>
                  </div>
                  <CardDescription className="leading-relaxed">L’Accessibility Passport è il profilo di accessibilità della tua struttura basato sulle informazioni che i nostri operatori hanno rilevato in loco. Espone in modo chiaro e oggettivo le caratteristiche di accessibilità dei tuoi spazi e servizi.</CardDescription>
                </CardHeader>
              </Card>

              <Card className="flex-1 min-w-[320px] h-full">
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ShieldQuestion className="w-5 h-5" />
                    <CardTitle>Perché è utile per gli ospiti con disabilità ed esigenze specifiche</CardTitle>
                  </div>
                  <CardDescription className="leading-relaxed">
                    Ogni persona ha esigenze diverse. Grazie alle informazioni contenute nell’Accessibility Passport, gli ospiti possono valutare in modo autonomo se la tua struttura è adatta alle loro necessità.
                  </CardDescription>
                  
                </CardHeader>
              </Card>
              </div>
              <p className="text-sm font-medium text-black leading-relaxed max-w-5xl">
                    Le informazioni contenute nell'Accessibility Passport si basano su dati oggettivi e verificabili: questo permette agli ospiti di fare il match tra le proprie esigenze di accessibilità e i servizi realmente disponibili nella tua struttura.
                  </p>

              <Tabs value={widgetShareTab} onValueChange={setWidgetShareTab} className="w-full pt-4">
                <TabsList className="grid w-full grid-cols-2 "
                style={{ justifyItems:"center" }}>
                  <TabsTrigger 
                    value="widget"
                    data-branding={isVisitBergamo ? "visit" : "travelin"}
                    className="flex items-center gap-2 border transition-colors"
                  style={{
                    paddingInline: "10%",
                    borderColor: isVisitBergamo ? "black" : "#003c5d"
                  }}>
                    <Monitor className="w-4 h-4" />
                    Anteprima
                  </TabsTrigger>
                  <TabsTrigger 
                    value="share"
                    data-branding={isVisitBergamo ? "visit" : "travelin"}
                    className="flex items-center gap-2 border transition-colors"
                  style={{
                    paddingInline: "10%",
                    borderColor: isVisitBergamo ? "black" : "#003c5d"
                  }}>
                    <Share className="w-4 h-4" />
                    Condividi
                  </TabsTrigger>
                  {/* <TabsTrigger value="technical" className="flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Integrazione Tecnica
                  </TabsTrigger> */}
                </TabsList>

                <TabsContent value="widget" className="space-y-6 !bg-transparent">
                  {/* <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <h2 className="text-xl font-semibold">Personalizza il widget</h2>
                      <p className="text-s text-muted-foreground">
                        Scegli dimensione e lingua del widget prima di integrarlo nei tuoi canali digitali.
                      </p>
                    </div>
                    
                  </div> */}
                  {/* Widget Preview */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ padding: '3%' }}>
                    <div className="lg:col-span-2">
                      <div className="space-y-4" style={{marginBlockEnd:'20px'}}>
                      <WidgetCustomizer
                        widgetSize={widgetSize as any}
                        setWidgetSize={setWidgetSize as any}
                        colorTheme={colorTheme as any}
                        setColorTheme={setColorTheme as any}
                        language={language as any}
                        setLanguage={setLanguage as any}
                      />
                      {/* <QuickActionsCard /> TODO */}
                      {/* <WidgetStatsCard /> TODO */}
                    </div>
                      <WidgetPreview
                        structure={currentStructurePrivate}
                        onGoToPassport={onGoToPassport}
                        onGoToPassport2={onGoToPassport2}
                        onSwitchToShareTab={() => setWidgetShareTab('share')}
                        selectedStructureSlug={selectedStructureSlug}
                        fetchedStructure={fetchedStructure}
                        widgetSize={widgetSize as any}
                        colorTheme={colorTheme as any}
                        language={language as any}
                      />
                    </div>
                    
                  </div>
                </TabsContent>

                <TabsContent value="manage" className="space-y-6">
                  {/* Passport Management */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Gestione Contenuti Passaporto</CardTitle>
                        <CardDescription>Modifica e aggiorna le tue informazioni di accessibilità</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-s">Nome Struttura</label>
                          <Input defaultValue={getStructureName(currentStructurePrivate)} />
                        </div>

                        <div className="space-y-2">
                          <label className="text-s">Punti di Forza Accessibilità</label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(currentStructurePrivate?.public_infos?.titlets || []).map((strength: any, index: number) => (
                              <Badge key={index} variant="outline" className="text-s">
                                {strength}
                                <button className="ml-2 text-muted-foreground hover:text-destructive cursor-pointer">×</button>
                              </Badge>
                            ))}
                          </div>
                          <Input placeholder="Aggiungi un nuovo punto di forza di accessibilità..." />
                        </div>

                        <div className="space-y-2">
                          <label className="text-s">Informazioni Chiave sull'Accessibilità</label>
                          <Textarea
                            defaultValue={String(Object.entries(currentStructurePrivate?.public_infos?.paragraphs || {})[0]?.[1] || '')}
                            rows={4}
                          />
                        </div>

                        <Button className="w-full bg-gradient-travelin hover:bg-gradient-travelin-cool text-white">
                          Salva Modifiche
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Moduli di Accessibilità</CardTitle>
                        <CardDescription>Monitora il completamento di tutti i moduli di accessibilità</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {passportNavItems.map((item, index) => (
                            <div key={item.id} className="flex items-center justify-between p-2 border rounded">
                              <div className="flex items-center gap-2">
                                <item.icon className="w-4 h-4 text-muted-foreground" />
                                <span className="text-s">{item.label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {index < 8 ? (
                                  <Badge variant="default" className="text-ss bg-green-100 text-green-800">Completo</Badge>
                                ) : index < 15 ? (
                                  <Badge variant="default" className="text-ss bg-yellow-100 text-yellow-800">In Corso</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-ss">In Attesa</Badge>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                                  <Settings className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="share" className="space-y-6 !bg-transparent pt-4" style={{ paddingInline: '3%' }}>
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold">Condividi il tuo Accessibility Passport</h2>
                    <p className="text-s text-muted-foreground">
                      Puoi condividere il profilo di accessibilità della tua struttura tramite widget, link o materiali esportabili.
                    </p>
                  </div>
            
                  {/* Share and Export Options */}
                  <div className="flex flex-col gap-6 w-full" >
                    {/* Embed Widget Card - Full Width */}
                    <ShareEmbedCard
                      structureId={selectedStructureId || ''}
                      portal={cachedStructures.find(s => s.id === selectedStructureId)?.structure?.portal_administrators?.portal_name}
                      theme={colorTheme as any}
                      language={language as any}
                      size={widgetSize as any}
                    />

                    {/* PDF Download Button */}
                    <div className="flex justify-start">
                      <DownloadPassportButton
                        publicStructure={pdfStructure}
                        structureName={
                          publicStructureDoc?.structure?.accommodation_data?.accommodation_name ||
                          publicStructureDoc?.name ||
                          'Struttura'
                        }
                        variant="outline"
                        className="justify-start"
                        disabled={!publicStructureDoc}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Scarica PDF
                      </DownloadPassportButton>
                    </div>
                    <p className="text-s text-muted-foreground">
                      Comunicare informazioni di accessibilità chiare e verificabili aiuta gli ospiti a scegliere con maggiore sicurezza e migliora la trasparenza della tua offerta.
                    </p>
                  </div>
                </TabsContent>

                {/* Technical Integration */}
                {/* <TabsContent value="technical" className="space-y-6">
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Code className="w-5 h-5" />
                          Website Integration
                        </CardTitle>
                        <CardDescription>Embed your accessibility widget on your website</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-s">Embed Code (HTML)</label>
                          <div className="relative">
                            <Textarea
                              readOnly
                              rows={6}
                              value={`<iframe 
  src="https://travelin.app/widget/accademia-carrara-bergamo" 
  width="400" 
  height="200" 
  frameborder="0" 
  title="Accessibility Information">
</iframe>`}
                              className="font-mono text-ss"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="absolute top-2 right-2"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-s">JavaScript Widget</label>
                          <div className="relative">
                            <Textarea
                              readOnly
                              rows={4}
                              value={`<script src="https://travelin.app/widget.js"></script>
<div id="travelin-widget" data-venue="accademia-carrara-bergamo"></div>`}
                              className="font-mono text-ss"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="absolute top-2 right-2"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h4 className="text-s mb-3">Integration Options</h4>
                          <div className="space-y-2">
                            <Button variant="outline" size="sm" className="w-full justify-start">
                              <Globe className="w-4 h-4 mr-2" />
                              WordPress Plugin
                            </Button>
                            <Button variant="outline" size="sm" className="w-full justify-start">
                              <Code className="w-4 h-4 mr-2" />
                              API Documentation
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Settings2 className="w-5 h-5" />
                          Advanced Settings
                        </CardTitle>
                        <CardDescription>Configure technical aspects of your passport</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-s">Public API Access</p>
                              <p className="text-ss text-muted-foreground">Allow third-party access to data</p>
                            </div>
                            <input type="checkbox" className="rounded" defaultChecked />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-s">Auto-sync Updates</p>
                              <p className="text-ss text-muted-foreground">Automatically update embedded widgets</p>
                            </div>
                            <input type="checkbox" className="rounded" defaultChecked />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-s">Analytics Tracking</p>
                              <p className="text-ss text-muted-foreground">Track widget performance</p>
                            </div>
                            <input type="checkbox" className="rounded" defaultChecked />
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h4 className="text-s mb-3">API Keys</h4>
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input value="trvl_sk_1234567890abcdef" readOnly className="font-mono text-ss" />
                              <Button size="icon" variant="outline">
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                            <Button size="sm" variant="outline" className="w-full">
                              Regenerate API Key
                            </Button>
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h4 className="text-s mb-3">Compliance & Security</h4>
                          <div className="space-y-2">
                            <Badge variant="outline" className="text-ss">GDPR Compliant</Badge>
                            <Badge variant="outline" className="text-ss">WCAG 2.1 AA</Badge>
                            <Badge variant="outline" className="text-ss">SSL Secured</Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent> */}
              </Tabs>
            </div>
          )}

          {structuresList.length > 0 && activeTab === "monitor" && (
            <div className="space-y-6">
              {/* Header Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    <CardTitle>Monitora il livello di accessibilità della tua struttura</CardTitle>
                  </div>
                  <CardDescription>Qui puoi vedere il livello di accessibilità della tua struttura nelle diverse aree (motoria, visiva, uditiva, cognitiva e alimentare). I dati si aggiornano automaticamente quando completi nuove dichiarazioni o miglioramenti.</CardDescription>
                </CardHeader>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle><b>Il tuo livello di accessibilità</b></CardTitle>
                    <CardDescription>Questo grafico mostra il livello di conformità della tua struttura per ciascuna area di accessibilità. Più il valore è alto, maggiore è la capacità della tua struttura di accogliere ospiti con esigenze diverse.</CardDescription>
                    <div className="mt-1 text-ss text-muted-foreground">
                      Fonte dati: Travelin - {monitorAccessData ? 'Firestore (aggiornato)' : privateAccessibilityData ? 'Firestore (in cache)' : 'Non disponibile'}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ComplianceBarChart data={currentAccessibilityData as any} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle><b>Miglioramenti prioritari</b></CardTitle>
                    <CardDescription>Qui trovi le aree che richiedono maggiore attenzione. Migliorare questi aspetti può aumentare il livello di accessibilità della tua struttura e la sua visibilità per gli ospiti che cercano servizi accessibili.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <UrgentImprovementList
                      urgentCategories={urgentImprovementCategories as any}
                      onGoToImprove={() => {
                        improveEntryRef.current = 'monitor';
                        setImproveViewMode('category');
                        setActiveTab('improve');
                      }}
                    />
                  </CardContent>
                </Card>
              </div>

              {isSuggestionsV2Enabled && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <CardTitle><b>Stato dei miglioramenti</b></CardTitle>
                      {suggestionsV2Summary?.source && getSourceBadge(suggestionsV2Summary.source)}
                    </div>
                    <CardDescription>Monitora l'avanzamento degli interventi di miglioramento che hai avviato. Puoi gestire ogni azione nella sezione <b>Migliora</b>.
</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {suggestionsV2SummaryLoading ? (
                      <div className="space-y-3">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-5/6" />
                      </div>
                    ) : suggestionsV2Summary?.source === 'none' || !suggestionsV2Summary?.total ? (
                      <div className="rounded-lg border border-dashed p-6 text-center">
                        <p className="text-s text-muted-foreground">Nessun intervento disponibile.</p>
                        <p className="mt-2 text-ss text-muted-foreground">Suggerimento: genera o importa le suggestions per avviare il monitoraggio.</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="text-xxl font-semibold">
                              {suggestionsV2Summary?.completionRate ?? 0}%
                            </div>
                            <p className="text-ss text-muted-foreground">Completion rate (esclusi scartati)</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-ss">
                              {suggestionsV2Summary?.byStatus.done}/{suggestionsV2Summary?.total} completati
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="h-2 w-full rounded-full bg-muted">
                            <div
                              className="h-2 rounded-full bg-gradient-travelin"
                              style={{ width: `${suggestionsV2Summary?.completionRate ?? 0}%` }}
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(['todo', 'in_progress', 'verifica_in_corso', 'done', 'rejected'] as SuggestionStatus[]).map((status) => (
                              <Badge key={status} className={`${getV2StatusBadgeClass(status)} text-ss`}>
                                {getV2StatusLabel(status)}: {suggestionsV2Summary?.byStatus[status] ?? 0}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-3">
                          {suggestionsV2Summary?.byCategory.filter((item) => item.total > 0).map((item) => {
                            // Compute dominant status from server counts
                            const statusCounts = {
                              todo: item.todo,
                              in_progress: item.in_progress,
                              verifica_in_corso: item.verifica_in_corso,
                              done: item.done,
                              rejected: item.rejected,
                            };
                            const order: SuggestionStatus[] = ['done', 'in_progress', 'verifica_in_corso', 'todo', 'rejected'];
                            let dominantStatus: SuggestionStatus = 'todo';
                            let maxCount = 0;
                            order.forEach((status) => {
                              if (statusCounts[status] > maxCount) {
                                maxCount = statusCounts[status];
                                dominantStatus = status;
                              }
                            });

                            return (
                              <div key={item.category} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
                                <div>
                                  <div className="text-s font-medium">{item.category}</div>
                                  <div className="text-ss text-muted-foreground">{item.done}/{item.total} completati</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={getV2StatusBadgeClass(dominantStatus)}>
                                    {getV2StatusLabel(dominantStatus)}
                                  </Badge>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      improveEntryRef.current = 'monitor';
                                      setImproveCategoryFilter(item.category);
                                      setImproveStatusFilter('all');
                                      setImproveViewMode('category');
                                      setImproveSearchQuery('');
                                      setActiveTab('improve');
                                    }}
                                    className="px-4 py-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
                                  >
                                    Apri Migliora
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

             {/*  <Card className="pointer-events-none select-none" style={{ opacity: 0.2 }}>
                <CardHeader>
                  <CardTitle>Requisiti Riassunto</CardTitle>
                  <CardDescription>Panoramica dei requisiti completati vs mancanti per categoria</CardDescription>
                </CardHeader>
                <CardContent>
                  <RequirementsSummaryGrid data={currentAccessibilityData as any} />
                </CardContent>
              </Card> */}
            </div>
          )}

          {structuresList.length > 0 && activeTab === "improve" && (
            <div className="space-y-6">
              {/* Header Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    <CardTitle>Migliora l'accessibilità della tua struttura</CardTitle>
                  </div>
                  <CardDescription>Qui trovi una lista di azioni pratiche per rendere la tua struttura più accessibile. Gli interventi sono organizzati per categoria e puoi monitorarne lo stato nel tempo.</CardDescription>
                </CardHeader>
              </Card>

              {isSuggestionsV2Enabled ? (
                <div className="space-y-6">
                  <Card>
                    <CardContent className="p-6 space-y-4">
                      {(() => {
                        const categories = suggestionsV2?.categories || [];
                        const allItems = categories.flatMap((category) =>
                          category.items.map((item) => ({ ...item, category: category.category }))
                        );
                        const totalCount = allItems.length;
                        const statusCounts: Record<SuggestionStatus, number> = {
                          todo: 0,
                          in_progress: 0,
                          verifica_in_corso: 0,
                          done: 0,
                          rejected: 0,
                        };
                        allItems.forEach((item) => {
                          statusCounts[item.status] += 1;
                        });
                        const effectiveTotal = Math.max(0, totalCount - statusCounts.rejected);
                        const progressPercent = effectiveTotal > 0
                          ? Math.round((statusCounts.done / effectiveTotal) * 100)
                          : 0;

                        return (
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <div className="text-xxl font-bold text-gradient-travelin">{progressPercent}%</div>
                                <p className="text-s text-muted-foreground">Completion rate (esclusi scartati)</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-ss">{statusCounts.done}/{effectiveTotal} completati</Badge>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="h-3 w-full rounded-full bg-muted">
                                <div
                                  className="h-3 rounded-full bg-gradient-travelin transition-all"
                                  style={{ width: `${progressPercent}%` }}
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(['todo', 'in_progress', 'verifica_in_corso', 'done', 'rejected'] as SuggestionStatus[]).map((status) => (
                                  <Badge 
                                    key={status} 
                                    className={`${getV2StatusBadgeClass(status)} text-ss cursor-pointer hover:opacity-80 transition-opacity`}
                                    onClick={() => setImproveStatusFilter(status)}
                                  >
                                    {getV2StatusLabel(status)}: {statusCounts[status]}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center">
                          <div className="flex items-center gap-2">
                            <label className="text-s text-muted-foreground">Stato</label>
                            <Select value={improveStatusFilter} onValueChange={(value) => setImproveStatusFilter(value as 'all' | SuggestionStatus)}>
                              <SelectTrigger className="w-44">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Tutti</SelectItem>
                                <SelectItem value="todo">Da fare</SelectItem>
                                <SelectItem value="in_progress">In corso</SelectItem>
                                <SelectItem value="verifica_in_corso">Verifica in corso</SelectItem>
                                <SelectItem value="done">Completato</SelectItem>
                                <SelectItem value="rejected">Scartato</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"  />
                            <Input
                              value={improveSearchQuery}
                              onChange={(event) => setImproveSearchQuery(event.target.value)}
                              placeholder="Cerca interventi"
                              style={{paddingInline: "3.5%"}}
                            />
                          </div>
                        </div>
                        <ToggleGroup
                          type="single"
                          variant="outline"
                          size="sm"
                          value={improveViewMode}
                          onValueChange={(value) => value && setImproveViewMode(value as 'category' | 'status')}
                        >
                          <ToggleGroupItem
                            value="category"
                            className="bg-white text-foreground border-border data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
                            style={{ padding: "1%" }}
                          >
                            Raggruppa per categoria
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="status"
                            className="bg-white text-foreground border-border data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
                            style={{ padding: "1%" }}
                          >
                            Vista per status
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                      {improveCategoryFilter !== 'all' && (
                        <div className="flex items-center gap-2" style={{paddingBlockStart: "20px"}}>
                          <Badge variant="outline" className="text-ss">Categoria: {improveCategoryFilter}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => setImproveCategoryFilter('all')}>
                            Rimuovi filtro
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Bulk Action Bar */}
                  {selectedItemIds.size > 0 && (
                    <Card className="border-2 border-blue-200 bg-blue-50">
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="text-s font-medium">{selectedItemIds.size} element{selectedItemIds.size > 1 ? 'i' : 'o'} selezionat{selectedItemIds.size > 1 ? 'i' : 'o'}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedItemIds(new Set())}
                            >
                              Deseleziona tutti
                            </Button>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBulkAction('todo')}
                              disabled={isBulkUpdating || suggestionsV2?.source !== 'collection'}
                            >
                              Segna come Da fare
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBulkAction('in_progress')}
                              disabled={isBulkUpdating || suggestionsV2?.source !== 'collection'}
                            >
                              Segna come In corso
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleBulkAction('done')}
                              disabled={isBulkUpdating || suggestionsV2?.source !== 'collection'}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Segna come Completato
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBulkAction('rejected')}
                              disabled={isBulkUpdating || suggestionsV2?.source !== 'collection'}
                            >
                              Segna come Scartato
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {suggestionsV2Loading ? (
                    <Card>
                      <CardContent className="p-6 space-y-4">
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                        <Skeleton className="h-4 w-4/6" />
                      </CardContent>
                    </Card>
                  ) : suggestionsV2Error ? (
                    <Card>
                      <CardContent className="p-6 text-s text-muted-foreground">
                        {suggestionsV2Error}
                      </CardContent>
                    </Card>
                  ) : (() => {
                    const query = normalizeSearchQuery(improveSearchQuery);
                    const categoryFilterValue = normalizeSearchQuery(improveCategoryFilter);
                    const statusFilterValue = improveStatusFilter;
                    const categories = suggestionsV2?.categories || [];
                    const filteredCategories = categories
                      .filter((category) => {
                        if (!categoryFilterValue || categoryFilterValue === 'all') return true;
                        return category.category.toLowerCase().includes(categoryFilterValue);
                      })
                      .map((category) => {
                      const filteredItems = category.items
                        .filter((item) => matchesSearch(item.text, query))
                        .filter((item) => statusFilterValue === 'all' || item.status === statusFilterValue)
                        .sort((a, b) => {
                          const order = ['todo', 'in_progress', 'verifica_in_corso', 'done', 'rejected'];
                          return order.indexOf(a.status) - order.indexOf(b.status);
                        });
                      return { ...category, items: filteredItems };
                    });
                    const hasItems = filteredCategories.some((category) => category.items.length > 0);
                    const isReadOnly = suggestionsV2?.source !== 'collection';

                    if (!hasItems) {
                      return (
                        <Card>
                          <CardContent className="p-8 text-center">
                            <p className="text-s text-muted-foreground">Nessun intervento disponibile.</p>
                          </CardContent>
                        </Card>
                      );
                    }

                    if (improveViewMode === 'status') {
                      const allItems = categories.flatMap((category) =>
                        category.items.map((item) => ({ ...item, category: category.category }))
                      );
                      const filteredItems = allItems
                        .filter((item) => {
                          if (!categoryFilterValue || categoryFilterValue === 'all') return true;
                          return item.category.toLowerCase().includes(categoryFilterValue);
                        })
                        .filter((item) => matchesSearch(item.text, query))
                        .filter((item) => statusFilterValue === 'all' || item.status === statusFilterValue);
                      const statusGroups = (['todo', 'in_progress', 'verifica_in_corso', 'done', 'rejected'] as SuggestionStatus[])
                        .map((status) => ({
                          status,
                          items: filteredItems.filter((item) => item.status === status),
                        }))
                        .filter((group) => group.items.length > 0);

                      return (
                        <Accordion 
                          type="multiple" 
                          className="rounded-lg border bg-white"
                          value={openAccordionItems}
                          onValueChange={setOpenAccordionItems}
                        >
                          {statusGroups.map((group) => {
                            const groupItemIds = group.items.map(item => item.id);
                            const allGroupSelected = groupItemIds.length > 0 && groupItemIds.every(id => selectedItemIds.has(id));
                            const someGroupSelected = groupItemIds.some(id => selectedItemIds.has(id));
                            
                            return (
                              <AccordionItem key={group.status} value={group.status}>
                                <AccordionTrigger className="px-4">
                                  <div className="flex items-center gap-3">
                                    {!isReadOnly && (
                                      <Checkbox
                                        checked={allGroupSelected}
                                        onCheckedChange={() => toggleSelectAll(groupItemIds)}
                                        onClick={(e) => e.stopPropagation()}
                                        className={someGroupSelected && !allGroupSelected ? 'data-[state=checked]:bg-gray-400' : ''}
                                      />
                                    )}
                                    <Badge className={getV2StatusBadgeClass(group.status)}>{getV2StatusLabel(group.status)}</Badge>
                                    <span className="text-ss text-muted-foreground">{group.items.length} interventi</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-4">
                                  <div className="space-y-3">
                                    {group.items.map((item) => (
                                      <div key={item.id} className="rounded-lg border p-4">
                                        <div className="flex items-start gap-3">
                                          {!isReadOnly && (
                                            <Checkbox
                                              checked={selectedItemIds.has(item.id)}
                                              onCheckedChange={() => toggleItemSelection(item.id)}
                                              className="mt-1"
                                            />
                                          )}
                                          <div className="flex-1 space-y-2">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                              <div className="space-y-1 flex-1">
                                                <p className="text-s font-medium">{item.text}</p>
                                                <span className="text-ss text-muted-foreground">Categoria: {item.category}</span>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {!isReadOnly && item.status === 'verifica_in_corso' && (
                                                  <Button
                                                    variant="default"
                                                    size="sm"
                                                    disabled
                                                    className="h-7 text-ss bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100"
                                                  >
                                                    ⏳ Verifica in corso
                                                  </Button>
                                                )}
                                                {!isReadOnly && item.status !== 'done' && item.status !== 'verifica_in_corso' && (
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleRequestVerification(item)}
                                                    disabled={v2UpdatingItems[item.id]}
                                                    className="h-7 text-ss"
                                                  >
                                                    Richiedi Verifica
                                                  </Button>
                                                )}
                                                <Select
                                                  value={item.status}
                                                  onValueChange={(value) => updateV2ItemStatus(item.id, value as SuggestionStatus)}
                                                  disabled={isReadOnly || v2UpdatingItems[item.id]}
                                                >
                                                  <SelectTrigger className="w-36">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="todo">Da fare</SelectItem>
                                                    <SelectItem value="in_progress">In corso</SelectItem>
                                                    <SelectItem value="verifica_in_corso">Verifica in corso</SelectItem>
                                                    <SelectItem value="done">Completato</SelectItem>
                                                    <SelectItem value="rejected">Scartato</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}  
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            );
                          })}
                        </Accordion>
                      );
                    }

                    return (
                      <Accordion 
                        type="multiple" 
                        className="rounded-lg border bg-white"
                        value={openAccordionItems}
                        onValueChange={setOpenAccordionItems}
                      >
                        {filteredCategories.map((category) => {
                          const categoryItemIds = category.items.map(item => item.id);
                          const allCategorySelected = categoryItemIds.length > 0 && categoryItemIds.every(id => selectedItemIds.has(id));
                          const someCategorySelected = categoryItemIds.some(id => selectedItemIds.has(id));

                          return (
                            <AccordionItem key={category.category} value={category.category}>
                              <AccordionTrigger className="px-4">
                                <div className="flex items-center gap-3">
                                  {!isReadOnly && (
                                    <Checkbox
                                      checked={allCategorySelected}
                                      onCheckedChange={() => toggleSelectAll(categoryItemIds)}
                                      onClick={(e) => e.stopPropagation()}
                                      className={someCategorySelected && !allCategorySelected ? 'data-[state=checked]:bg-gray-400' : ''}
                                    />
                                  )}
                                  <span className="text-s font-semibold">{category.category}</span>
                                  <Badge variant="secondary" className="text-ss">{category.items.length} interventi</Badge>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-4">
                                <div className="space-y-3">
                                  {category.items.map((item) => (
                                    <div key={item.id} className="rounded-lg border p-4">
                                      <div className="flex items-start gap-3">
                                        {!isReadOnly && (
                                          <Checkbox
                                            checked={selectedItemIds.has(item.id)}
                                            onCheckedChange={() => toggleItemSelection(item.id)}
                                            className="mt-1"
                                          />
                                        )}
                                        <div className="flex-1">
                                          <div className="flex flex-wrap items-start justify-between gap-3">
                                            <p className="text-s font-medium flex-1">{item.text}</p>
                                            <div className="flex items-center gap-2">
                                              {!isReadOnly && item.status === 'verifica_in_corso' && (
                                                <Button
                                                  variant="default"
                                                  size="sm"
                                                  disabled
                                                  className="h-7 text-ss bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100"
                                                >
                                                  ⏳ Verifica in corso
                                                </Button>
                                              )}
                                              {!isReadOnly && item.status !== 'done' && item.status !== 'verifica_in_corso' && (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => handleRequestVerification(item)}
                                                  disabled={v2UpdatingItems[item.id]}
                                                  className="h-7 text-ss"
                                                >
                                                  Richiedi Verifica
                                                </Button>
                                              )}
                                              <Select
                                                value={item.status}
                                                onValueChange={(value) => updateV2ItemStatus(item.id, value as SuggestionStatus)}
                                                disabled={isReadOnly || v2UpdatingItems[item.id]}
                                              >
                                                <SelectTrigger className="w-36">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="todo">Da fare</SelectItem>
                                                  <SelectItem value="in_progress">In corso</SelectItem>
                                                  <SelectItem value="done">Completato</SelectItem>
                                                  <SelectItem value="rejected">Scartato</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    );
                  })()}
                </div>
              ) : (
                <div className="flex min-h-[600px] w-full border border-border rounded-lg overflow-hidden bg-white">
                  {/* Left Navigation Menu */}
                  <ImprovementSidebar
                    categories={accessibilityCategories as any}
                    selectedId={selectedImprovementCategory}
                    onSelect={(id) => setSelectedImprovementCategory(id)}
                  />

                  {/* Right Content Area */}
                  <div className="flex-1 flex flex-col min-w-0">
                    <div className="p-6 space-y-6">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                              <label className="text-s text-muted-foreground">Filtra per Stato:</label>
                              <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Tutti gli Stati</SelectItem>
                                  <SelectItem value="not_started">Non iniziato</SelectItem>
                                  <SelectItem value="in_progress">In corso</SelectItem>
                                  <SelectItem value="completed">Completato</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="text-s text-muted-foreground">
                              Mostrati {filteredSuggestions.length} di {privateSuggestions.length} miglioramenti
                            </div>
                            {selectedImprovementCategory !== "all" && (
                              <Badge variant="outline" className="text-ss">
                                Categoria: {accessibilityCategories.find(cat => cat.id === selectedImprovementCategory)?.label}
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <div className="space-y-4">
                        <ModuleSuggestionsList
                          modules={improvementModules as any}
                          statusFilter={normalizedStatusFilter}
                          getStatusColor={getStatusColor}
                          onManage={handleManageSuggestion}
                          onRequestVerification={handleRequestVerification}
                          isSaving={isSavingSuggestions}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Manage Suggestion Dialog */}
      <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Gestisci Miglioramenti</DialogTitle>
            <DialogDescription>
              Aggiorna lo stato di questo miglioramento dell'accessibilità.
            </DialogDescription>
          </DialogHeader>
          {selectedSuggestion && (
            <div className="space-y-4">
              <div>
                <h4 className="text-s font-medium mb-2">Descrizione</h4>
                <p className="text-s text-muted-foreground leading-relaxed">
                  {selectedSuggestion.description}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-s font-medium">Stato</label>
                <Select value={newStatus} onValueChange={setNewStatus} disabled={isSavingSuggestions}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Non iniziato</SelectItem>
                    <SelectItem value="in_progress">In corso</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-ss ${selectedSuggestion.priority === 'high'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : selectedSuggestion.priority === 'medium'
                    ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                    : 'border-gray-200 bg-gray-50 text-gray-700'
                  }`}>
                  {selectedSuggestion.priority} priorità
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageDialogOpen(false)} disabled={isSavingSuggestions}>
              Annulla
            </Button>
            <Button onClick={handleSaveStatus} className="bg-gradient-travelin hover:bg-gradient-travelin-cool text-white" disabled={isSavingSuggestions}>
              {isSavingSuggestions ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkActionDialog.open} onOpenChange={(open) => setBulkActionDialog({ open, action: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Conferma azione massiva</DialogTitle>
            <DialogDescription>
              Stai per modificare lo stato di <strong>{selectedItemIds.size}</strong> interventi.
              {bulkActionDialog.action && (
                <span className="block mt-2">
                  Nuovo stato: <Badge className={getV2StatusBadgeClass(bulkActionDialog.action)}>{getV2StatusLabel(bulkActionDialog.action)}</Badge>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-s text-muted-foreground">
              Questa operazione modificherà contemporaneamente {selectedItemIds.size} interventi. 
              Vuoi continuare?
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setBulkActionDialog({ open: false, action: null })}
              disabled={isBulkUpdating}
            >
              Annulla
            </Button>
            <Button 
              onClick={confirmBulkAction} 
              disabled={isBulkUpdating}
              className="bg-gradient-travelin hover:bg-gradient-travelin-cool text-white"
            >
              {isBulkUpdating ? 'Aggiornamento...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
